<template>
  <div class="reports">
    <layout-logged-frame title="Reports" has-before>
      <template v-slot:header-actions>
        <header-actions :actions="headerActions" />
      </template>

      <template v-slot:before-content>
        <div class="reports-before-content mb-6">
          <v-skeleton-loader
              v-if="isEngagementsLoading"
              type="table-cell"
              class="reports-before-content__loader"
          />
          <template v-else>{{ $t('updated') + ' ' + updatedAt }}</template>
        </div>
      </template>

      <template v-slot:content>
        <ReportFilters
            class="mb-6"
            @filter="onFilter"
            @clearFilters="onClearFilters"
            :is-skeleton-loading="isSkeletonLoading"
        >
          <template v-slot:phone-actions>
            <sort-by-select
              :columns="columns"
              :pagination="pagination"
              :on-update-pagination="onUpdatePagination"
            />
          </template>
        </ReportFilters>

        <Metrics
          :is-engagements-loading="isEngagementsLoading"
          :engagements="engagements"
          class="mb-4"
        />

        <div class="d-flex full">
          <ReportsChart
              :is-loading="isChartSummaryLoading"
              :data="chartSummary"
          />

          <SidePanelReports
              class="ml-4"
              :topDeals="topDeals"
              :is-loading="isTopDealsLoading"
          />
        </div>

        <div class="reports-title">
          {{ $t('deals') }}
        </div>

        <ReportsTable
            :items="deals"
            :pagination="pagination"
            :on-select-item="selectDeal"
            :total-pages="totalPages"
            :on-update-pagination="onUpdatePagination"
            :sync-to-url="true"
            @init-pagination="onInitPagination"
            :on-change-page="onChangePage"
            :is-loading="isDealsLoading"
        />

        <div class="reports-title">
          {{ $t('additional_clickouts') }}
        </div>

        <div class="reports-subtitle">
          {{ $t('these_clickouts_are_not_included') }}
        </div>

        <ClickoutsTable
          :items="clickouts"
          :is-loading="isClickoutsLoading"
        />
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
import {isStr} from '@/utils';
import columns from '../../config/reports/columnsPerformance';
import ReportFilters from '../../components/forms/filters/ReportsPerformance';
import urlPaginationRestore from '@/mixins/urlPaginationRestore';
import ReportsChart from '../../components/charts/Reports';
import SidePanelReports from "../../components/panels/reports/Side.vue";
import ReportsTable from "../../components/table/reports/Index.vue";
import Metrics from '../../components/panels/reports/Metrics.vue';
import ClickoutsTable from "@/components/table/reports/Clickouts.vue";

export default {
  mixins: [urlPaginationRestore],
  components: {
    ClickoutsTable,
    SidePanelReports,
    ReportsChart,
    ReportFilters,
    ReportsTable,
    Metrics
  },
  data() {
    return {
      columns,
      clickouts: [],
      isClickoutsLoading: false,
      isSkeletonLoading: false,
      isEngagementsLoading: false,
      isDealsLoading: false,
      isChartSummaryLoading: false,
      isTopDealsLoading: false,
      isDownloadingReport: false,
      pagination: {
        order: 'desc' ,
        page: 1,
        rowsPerPage: 15,
      },
      query: {},
      queryWithPagination: {},
      totalPages: 0,
    };
  },
  computed: {
    headerActions() {
      return [
        {
          key: 'download',
          icon: 'file_download',
          iconClass: 'material-icons-outlined',
          title: 'Download CSV',
          isLoading: this.isDownloadingReport,
          onClick: this.downloadReport,
        },
      ];
    },
    engagements() {
      const val = this.$store.getters['reports/engagements'];
      if (val?.length) {
        return val[0];
      }
      return null;
    },
    deals() {
      return this.$store.getters['reports/deals'];
    },
    topDeals() {
      return this.$store.getters['reports/topDeals'];
    },
    chartSummary() {
      return this.$store.getters['reports/chartSummary'];
    },
    updatedAt() {
      return this.engagements?.updated_at ?
          this.$dayjs(this.engagements.updated_at).fromNow()
          : '';
    },
  },
  methods: {
    onFilter(filters) {
      this.isSkeletonLoading = true;

      this.query = {
        ...this.query,
        ...filters,
      };

      this.queryWithPagination = {
        ...this.query,
        ...this.pagination,
      };

      this.runInitialFetch(() => {
        this.getEngagements();
        this.getDeals();
      });
      this.getChartSummary();
      this.getTopDeals();
      this.getClickouts();
    },
    onUpdatePagination({page, order, sortBy}) {
      const {queryWithPagination, pagination} = this;

      // prevent pagination request if page is the same
      // to prevent double request on first load
      if (page === pagination.page && order === pagination.order) return;

      this.pagination.order = order;
      this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

      this.queryWithPagination = {
        ...queryWithPagination,
        ...query,
        page,
        order,
        sort: isStr(sortBy) ? sortBy : pagination.sortBy
      };

      this.getDeals();
    },
    onChangePage(page) {
      const { query, pagination } = this;
      const { order, sortBy } = pagination;
      this.queryWithPagination = {
        ...query,
        page,
        order,
        sort: sortBy,
      };

      this.getDeals();
    },
    downloadReport() {
      this.isDownloadingReport = true;
      return this.$store
          .dispatch('reports/downloadPerformanceReport', this.query)
          .then((res) => {
            this.isDownloadingReport = false;
            return res;
          })
          .catch((err) => {
            this.isDownloadingReport = false;
            return err;
          });
    },
    selectDeal(val) {
      this.$router.push({
        name: 'reports-details',
        params: { id: val.thread_id },
        query: this.$route.query,
      });
    },
    getClickouts() {
      this.isClickoutsLoading = true;

      const query = {
        date_range: this.query.date_range,
        countries: this.query.countries,
      }

      return this.$store
        .dispatch('reports/getClickouts', query)
        .then((res) => {
          this.clickouts = res;
        })
        .finally(() => {
          this.isClickoutsLoading = false;
        });
    },
    getEngagements() {
      this.isEngagementsLoading = true;
      return this.$store
          .dispatch('reports/getEngagements', this.query)
          .finally(() => {
            this.isEngagementsLoading = false;
            this.isSkeletonLoading = false;
          });
    },
    getDeals() {
      this.isDealsLoading = true;
      return this.$store
          .dispatch('reports/getDeals', this.queryWithPagination)
          .then((res) => {
            const { total, current_page, last_page } = res.meta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
          })
          .finally(() => {
            this.isDealsLoading = false;
            this.isSkeletonLoading = false;
          });
    },
    getChartSummary() {
      this.isChartSummaryLoading = true;
      return this.$store
          .dispatch('reports/getChartSummary', this.query)
          .finally(() => {
            this.isChartSummaryLoading = false;
            this.isSkeletonLoading = false;
          });
    },
    getTopDeals() {
      this.isTopDealsLoading = true;
      return this.$store
          .dispatch('reports/getTopDeals', this.query)
          .finally(() => {
            this.isTopDealsLoading = false;
            this.isSkeletonLoading = false;
          });
    },
    onClearFilters() {
      const { sortBy } = this.pagination;
      this.query = { sort: sortBy };
      this.onFilter();
    },
  },
};
</script>

<style lang="scss">
@use 'sass:map';
@use "../../assets/styles/theme" as *;

.reports-before-content {
  display: flex;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  color: map.get($colors, text-muted) !important;
}

.reports-before-content__loader {
  height: 18px;

  .v-skeleton-loader__table-cell {
    height: auto;
  }
}

.reports-title {
  font-size: $text-size-xl;
  font-weight: 700;
  margin-top: 24px;
  margin-bottom: 8px;
}

.reports-subtitle {
  color: rgba(var(--v-theme-on-surface), 0.38);
  font-size: $text-size-sm;
  font-weight: 400;
  margin-bottom: 8px;
}
</style>
